import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { IUser } from "services/types";
import { routes } from "routes";
import { nFormatter } from "utils/parseNumber";
import { FollowButton, UnFollowButton } from "components/buttons/FollowUnfollowButtons";
import { isAuthenticated } from "services/Auth";
import { VerifiedButton } from "./VerifiedButton";
import { apiMain } from "services";
import DEFAULT_USERPIC from "components/icons/default_userpic.svg";
import ProfileDropdownMenu from "./ProfileDropdownMenu";
import ProfileMenuPopup from "./ProfileMenuPopup";
import VerifiedUserIcon from "components/UI/VerifiedUserIcon";
import { LINK_REGEXP } from "pages/VerifyPage/constants";
import { showAuthModal } from "store/application/application-action";
import { useFollowingQuery } from "queries";
import "./CreatorInfo.scss";
import { metrics } from "utils/metrics";
import { v4 as uuidv4 } from "uuid";
import { Links } from "./Links";

interface IProps {
  user: IUser | undefined;
  myself: boolean;
}

const socialName = (url: any) => {
  let socialKey: string | null = null;

  for (const [key, { regExp }] of Object.entries(LINK_REGEXP)) {
    if (regExp.test(url)) {
      socialKey = key;
    }
  }

  return socialKey;
};

export const socialAdapter = (user: any) => {
  const socials = [];
  const filterKey = "socialUrl";

  for (const [key, val] of Object.entries(user)) {
    const name = socialName(val);
    // @ts-ignore
    const isSocial = key.includes(filterKey) && val?.length && name;

    if (isSocial) {
      const social: any = { name, url: val, key };

      socials.push(social);
    }
  }

  return socials;
};

export const CreatorInfo = (props: IProps) => {
  const {
    myself,
    user: {
      verified = false,
      profileImageUrl = "",
      name = "",
      username = "",
      links = [],
      publishedCollections = 1,
      publishedGifs = 1,
      followers = 1,
      views = 1,
      description = "",
    } = {}
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isFollowing, setFollowing] = useState<boolean>(false);

  const { data: { follows = [] } = {} } = useFollowingQuery({});

  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);

  const canFollow = !myself && !isFollowing;
  const canUnfollow = !myself && isFollowing;
  const canVerify = myself && !verified;
  const userInfoData = [
    {
      title: "Followers",
      count: followers,
      link: routes.followers()
    },
    {
      title: "Views",
      count: views
    },
    {
      title: "Posts",
      count: publishedGifs
    },
  ];

  const handleFollow = () => {
    isAuthenticated().then((isLoggedIn) => {
      if (isLoggedIn) {
        console.debug(`Following ${username}`);
        setFollowing(true);

        apiMain.followUser(username).then((res) => {
          console.debug(`Successfully followed ${username}`);
        }).catch(() => {
          setFollowing(false);
          console.error(`Could not follow ${username}`);
        });
        metrics.sendFollowUserClicked({ userName: username });
      } else {
        dispatch(showAuthModal());
      }
    });
  };

  const handleUnfollow = () => {
    console.debug(`Unfollowing ${username}`);
    setFollowing(false);

    apiMain.unfollowUser(username).then((res) => {
      console.debug(`Successfully unfollowed ${username}`);
    }).catch(() => {
      setFollowing(true);
      console.error(`Could not unfollow ${username}`);
    });
  };

  const handleMenu = () => {
    setIsOpenMenu((prev) => !prev);
  };

  const renderCollectionLink = () => {
    if (!publishedCollections) {
      return null;
    }

    return (
      <div className="CreatorInfo-Collections">
        <Link to={routes.userCollections(username)}>
          <span>{publishedCollections}</span> Collections
        </Link>
      </div>
    );
  };

  const renderMoreProfile = () => {
    return (
      <>
        <ProfileDropdownMenu
          tooglePopup={handleMenu}
          userName={username}
          myself={myself}
        />
        <ProfileMenuPopup
          isOpen={isOpenMenu}
          togglePopup={handleMenu}
          userName={username}
          myself={myself}
        />
      </>
    );
  };

  const renderUserTopInfo = () => {
    const userImg = profileImageUrl || DEFAULT_USERPIC;
    const userName = name ?? username;

    return (
      <div className="CreatorInfo-TopInfo">
        <img
          className="CreatorInfo-Avatar"
          src={userImg}
          alt={`User avatar for ${userName}`}
        />
        <div className="CreatorInfo-Titles">
          <div className="CreatorInfo-TitlesTop">
            <h1 className="userName" title={userName}>
              {userName}
              {verified && <VerifiedUserIcon userName={userName} />}
            </h1>
            <h2 className="shortUserName">@{username}</h2>
            {renderCollectionLink()}
          </div>
          <div className="CreatorInfo-TitlesBottom">
            {renderUnFollowButton()}
            {renderFollowButton()}
          </div>
        </div>
        {renderMoreProfile()}
      </div>
    );
  };

  const renderUserCount = ({ title, count, link }: { title: string; count: number; link?: string }) => (
    <div
      className="CreatorInfo-CounterItem"
      key={uuidv4()}
      title={new Intl.NumberFormat().format(count)}
      {...(link ? { onClick: () => navigate(link) } : {})}
    >
      <div className="h">{nFormatter(count, 1)}</div>
      <span>{title}</span>
    </div>
  );

  const renderUserCounter = () => {
    return (
      <div className="CreatorInfo-Counter">
        {userInfoData.map(renderUserCount.bind(this))}
      </div>
    );
  };

  const renderDescription = () => {
    if (!description) {
      return null;
    }

    return (
      <div className="CreatorInfo-Description">
        <h2>About {username}:</h2>
        <p>{description}</p>
      </div>
    );
  };

  const renderUnFollowButton = () => {
    if (!canUnfollow) {
      return null;
    } else if (isFollowing) {
      return (
        <UnFollowButton
          className="follow"
          variant="outlined"
          fullWidth
          onClick={handleUnfollow}
        >
          Unfollow
        </UnFollowButton>
      );
    }
  };

  const renderFollowButton = () => {
    if (!canFollow) {
      return null;
    } else if (!isFollowing) {
      return (
        <FollowButton
          className="follow"
          variant="outlined"
          fullWidth
          onClick={handleFollow}
        >
          Follow
        </FollowButton>
      );
    }
  };

  const renderVerifyButton = () => {
    if (!canVerify) {
      return null;
    }

    return <VerifiedButton />;
  };

  useEffect(() => {
    if (follows.length) {
      const isFollows = !!follows.find(f => f.username === username);
      setFollowing(isFollows);
    }
  }, [follows, username]);

  return (
    <div className="CreatorInfo">
      {renderUserTopInfo()}
      {renderUserCounter()}
      {renderDescription()}

      {links && links?.length > 0 && (
        <Links userName={username} links={links}/>
      )}
      {renderVerifyButton()}
    </div>
  );
};
